/** Builds a fully self-contained HTML document from the rendered report pages, suitable for
 *  POSTing to a headless-Chromium PDF service (Gotenberg) that has no network path back to
 *  the app: every same-origin stylesheet is inlined, and images and CSS url() assets (fonts,
 *  icons, logos) are embedded as data: URIs. Cross-origin resources are left
 *  untouched — the service renders without them rather than failing. */

const dataUriCache = new Map<string, Promise<string>>();

function toDataUri(url: string): Promise<string> {
  let cached = dataUriCache.get(url);
  if (!cached) {
    cached = (async () => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`${response.status} for ${url}`);
      }
      const blob = await response.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    })();
    dataUriCache.set(url, cached);
  }
  return cached;
}

function isSameOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

const cssUrlPattern = /url\((['"]?)([^)'"]+)\1\)/g;

/** Families installed as system fonts in the PDF container (see
 *  pdf-render-poc/docker-compose.yml). Their @font-face rules are stripped from the
 *  snapshot: an inlined data-URI web font would override the system font and force
 *  Chromium to embed bulky Type 3 vector glyphs instead of subsetted CID TrueType.
 *  @font-face rules for families NOT in this set are kept, so a newly added web font
 *  degrades to a bigger snapshot rather than a missing font - but bake it into the
 *  image and list it here to keep the payload minimal. */
const containerFontFamilies = new Set([
  'laica', 'geita', 'abc laica', 'store norske geita',
  'roboto', 'open sans', 'material icons', 'material icons outlined',
  'arial', 'courier new', 'times new roman',
  'aggridbalham',
]);

function isContainerFontFace(rule: CSSRule): boolean {
  if (rule.type !== CSSRule.FONT_FACE_RULE) {
    return false;
  }
  const family = (rule as CSSFontFaceRule).style.getPropertyValue('font-family')
    .replace(/['"]/g, '').trim().toLowerCase();
  return containerFontFamilies.has(family);
}

async function inlineCssUrls(css: string): Promise<string> {
  const urls = new Set<string>();
  let match: RegExpExecArray | null;
  cssUrlPattern.lastIndex = 0;
  while ((match = cssUrlPattern.exec(css)) !== null) {
    const url = match[2];
    if (!url.startsWith('data:') && !url.startsWith('#') && isSameOrigin(url)) {
      urls.add(url);
    }
  }
  const resolved = new Map<string, string>();
  await Promise.all(Array.from(urls).map(async url => {
    try {
      resolved.set(url, await toDataUri(url));
    } catch {
      // unreachable asset - keep the original reference
    }
  }));
  return css.replace(cssUrlPattern, (full, _quote, url: string) => {
    const dataUri = resolved.get(url);
    return dataUri ? `url(${dataUri})` : full;
  });
}

export async function buildReportHtmlSnapshot(container: HTMLElement): Promise<string> {
  const clone = container.cloneNode(true) as HTMLElement;

  // embed same-origin <img> sources
  await Promise.all(Array.from(clone.querySelectorAll('img')).map(async img => {
    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:') || !isSameOrigin(src)) {
      return;
    }
    try {
      img.setAttribute('src', await toDataUri(src));
    } catch {
      // unreachable image - keep the original reference
    }
  }));

  // collect every same-origin stylesheet the app has loaded (cssText comes back with
  // absolutized url() references, which inlineCssUrls then embeds). @font-face rules for
  // fonts the container has installed are dropped (see isContainerFontFace); the container
  // maps the app's CSS family names to the installed families via fontconfig aliases.
  let css = '';
  for (const sheet of Array.from(document.styleSheets)) {
    // reproduce the on-screen state: skip sheets that are not active on screen
    // (disabled alternates such as an unloaded theme, or media-scoped sheets)
    if (sheet.disabled || (sheet.media.mediaText && !window.matchMedia(sheet.media.mediaText).matches)) {
      continue;
    }
    try {
      css += Array.from(sheet.cssRules)
        .filter(rule => !isContainerFontFace(rule))
        .map(rule => rule.cssText).join('\n') + '\n';
    } catch {
      // cross-origin stylesheet - skip
    }
  }
  css = await inlineCssUrls(css);

  // Print setup is unconditional here, unlike the body-class-scoped rules in pdf-reports.scss:
  // this document contains nothing but report pages.
  //
  // The <html>/<body> classes are carried over because the clone only contains the pages
  // subtree: rules scoped to app-level classes (e.g. body.mdc-typography, which supplies the
  // Roboto font family everything inherits) would otherwise stop matching and Chromium would
  // fall back to its serif default font.
  return `<!DOCTYPE html>
<html class="${document.documentElement.className}">
<head>
<meta charset="utf-8">
<style>${css}</style>
<style>
  /* The Material Icons stylesheets are cross-origin (fonts.googleapis.com), so their class
     rules never make it into the inlined CSS above. The PDF container has the icon fonts
     installed as system fonts (see pdf-render-poc/docker-compose.yml) - no @font-face
     needed, but the class rules that map the classes to the font families must be
     re-created here. */
  .material-icons, .material-icons-outlined {
    font-weight: normal; font-style: normal; font-size: 24px; line-height: 1;
    letter-spacing: normal; text-transform: none; display: inline-block;
    white-space: nowrap; word-wrap: normal; direction: ltr;
    font-feature-settings: 'liga'; -webkit-font-feature-settings: 'liga';
    -webkit-font-smoothing: antialiased;
  }
  .material-icons { font-family: 'Material Icons'; }
  .material-icons-outlined { font-family: 'Material Icons Outlined'; }
</style>
<style>
  @page { size: A4 landscape; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .pdf-reports__pages-container { display: block; width: 29.7cm; }
  .pdf-reports__page-container { display: block; height: 20.98cm; overflow: hidden; break-after: page; }
  .pdf-reports__page-container:last-child { break-after: auto; }
  .report-page { margin: 0 !important; }
</style>
</head>
<body class="${document.body.className}">${clone.outerHTML}</body>
</html>`;
}

/** Renders the pages in `container` (a .pdf-reports__pages-container of
 *  .pdf-reports__page-container children, each holding a report page in its printed state)
 *  in the pdf-service (Gotenberg, headless Chromium; pdf-render-poc/) and downloads the PDF.
 *  The service gets a self-contained snapshot, so it needs no access or auth towards the app.
 *  /pdf-service is same-origin: the dev server proxy locally, the gateway route in a deployed
 *  environment. */
export async function printReportPdf(container: HTMLElement): Promise<void> {
  const html = await buildReportHtmlSnapshot(container);
  const form = new FormData();
  form.append('files', new File([html], 'index.html', { type: 'text/html' }));
  form.append('preferCssPageSize', 'true');
  form.append('printBackground', 'true');
  const response = await fetch('/pdf-service/forms/chromium/convert/html', { method: 'POST', body: form });
  if (!response.ok) {
    throw new Error(`PDF service responded ${response.status}: ${await response.text()}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'report.pdf';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000); // give the download time to start
}
